/**
 * ThemeToggle — flips between light and dark mode.
 *
 * Two presentations:
 *   - default (inline): a 28px icon button sized to sit in chrome like the
 *     AdminLayout top bar, styled with design tokens.
 *   - floating: a pill fixed to the top-right, for surfaces that have no chrome
 *     (login, shared non-admin routes) so the control is always reachable.
 *
 * Uses the design-system tokens only — no hardcoded colors.
 */

import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function ThemeToggle({ floating = false, className = '' }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const Icon = isDark ? Sun : Moon;
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  if (floating) {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={label}
        title={label}
        className={[
          'fixed top-4 right-4 z-50 flex items-center justify-center h-10 w-10 rounded-full',
          'bg-surface backdrop-blur border border-border text-text-muted shadow-md',
          'hover:text-text hover:border-border-strong transition-colors duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          className,
        ].join(' ')}
      >
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={[
        'flex items-center justify-center h-7 w-7 rounded-md',
        'text-text-disabled hover:bg-surface-sunken hover:text-text-muted transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
        className,
      ].join(' ')}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}
